import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, LayoutGrid } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

const categoryKeys = ['bitrix24', 'google_extensions', 'mobile_apps', 'web_tools'];

const PageHeader = ({ t }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-6">
      <Link to="/" className="flex items-center flex-shrink-0">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders';
          }}
        />
      </Link>
      <LanguageSwitcher variant="compact" />
    </div>
  </header>
);

const AppsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const categories = useMemo(
    () => categoryKeys.map((key) => t(`apps.categories.${key}`)),
    [t]
  );
  const allLabel = t('apps.all');
  const items = t('apps.items', { returnObjects: true });

  const apps = useMemo(
    () => ([
      {
        title: items?.[0]?.title ?? 'NexBot',
        description: items?.[0]?.description ?? 'Automatización conversacional en Bitrix24 con dashboard y gestión centralizada de bots.',
        href: '/nexbot',
        category: items?.[0]?.category ?? t('apps.categories.bitrix24'),
        imageSrc: '/images/blog/blog_nexia.png'
      },
      {
        title: items?.[1]?.title ?? 'API Tool',
        description: items?.[1]?.description ?? 'Consultas API desde los flujos de Bitrix24 para integrar sistemas y automatizar acciones críticas.',
        href: '/api-tool',
        category: items?.[1]?.category ?? t('apps.categories.bitrix24'),
        imageSrc: '/images/blog/blog_bitrix24.png'
      },
      {
        title: items?.[2]?.title ?? 'Posti',
        description: items?.[2]?.description ?? 'Herramienta exclusiva de gestión de redes sociales para potenciar el alcance 100% orgánico en LinkedIn y Facebook.',
        href: '/posti',
        category: items?.[2]?.category ?? t('apps.categories.bitrix24'),
        imageSrc: '/images/posti/1.png'
      },
      {
        title: items?.[3]?.title ?? 'NextShot',
        description: items?.[3]?.description ?? 'Extensión para capturar, editar y exportar capturas (PNG, PDF, ZIP).',
        href: '/nextshot',
        category: items?.[3]?.category ?? t('apps.categories.google_extensions'),
        imageSrc: '/images/nextshot/1.png'
      },
      {
        title: items?.[4]?.title ?? 'Notex',
        description: items?.[4]?.description ?? 'Aplicación móvil para registrar ideas, notas y tareas en segundos.',
        href: '/notex',
        category: items?.[4]?.category ?? t('apps.categories.web_tools'),
        imageSrc: '/images/notex/1.png'
      },
      {
        title: items?.[5]?.title ?? 'Infood',
        description: items?.[5]?.description ?? 'Aplicación móvil para gestionar información y flujos relacionados a alimentos.',
        href: '/infood',
        category: items?.[5]?.category ?? t('apps.categories.mobile_apps'),
        imageSrc: '/images/infood/1.png'
      },
      {
        title: items?.[6]?.title ?? 'NextPDF',
        description: items?.[6]?.description ?? 'Herramientas web para generar y gestionar PDFs de forma simple.',
        href: '/nextpdf',
        category: items?.[6]?.category ?? t('apps.categories.web_tools'),
        imageSrc: '/images/nextpdf/1.png'
      },
      {
        title: items?.[7]?.title ?? 'LockedPass',
        description: items?.[7]?.description ?? 'Gestor de contrasenas zero-knowledge para equipos, con cifrado de extremo a extremo y control total sobre credenciales compartidas.',
        href: 'https://lockedpass.com',
        category: items?.[7]?.category ?? t('apps.categories.web_tools'),
        imageSrc: '/images/lockedpass/cover.png',
        external: true
      },
      {
        title: items?.[8]?.title ?? 'WATOK',
        description: items?.[8]?.description ?? 'Integracion profesional para gestionar comunicaciones WABA con control operativo, automatizacion y seguridad.',
        href: '/watok',
        category: items?.[8]?.category ?? t('apps.categories.bitrix24'),
        imageSrc: '/images/watok/cover.png'
      }
    ]),
    [items, t]
  );

  const visibleApps = useMemo(() => {
    const hiddenHrefs = new Set(['/posti', '/notex', '/infood', '/nextpdf']);
    const filteredApps = apps.filter((app) => !hiddenHrefs.has(app.href));
    if (selectedCategory === 'all') return filteredApps;
    return filteredApps.filter((app) => app.category === selectedCategory);
  }, [apps, selectedCategory]);

  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      <PageHeader t={t} />
      <main className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('apps.back')}
          </button>

          <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 mb-10">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 bg-gray-50 text-gray-700 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-gray-200">
                  <LayoutGrid size={16} />
                  {t('apps.badge')}
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-[#141414] mb-3">{t('apps.title')}</h1>
                <p className="text-gray-600 max-w-3xl leading-relaxed">
                  {t('apps.subtitle')}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedCategory('all')}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                    selectedCategory === 'all'
                      ? 'bg-[#141414] text-white border-[#141414]'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {allLabel}
                </button>
                {categories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                      selectedCategory === category
                        ? 'bg-[#141414] text-white border-[#141414]'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {visibleApps.map((app) => {
              const cardContent = (
                <>
                  <div className={`relative ${app.imageWrapperClassName ?? 'bg-gray-50'}`}>
                    <img
                      src={app.imageSrc}
                      alt={app.title}
                      className={`w-full h-52 object-cover ${app.imageClassName ?? ''}`}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://placehold.co/800x500/f3f4f6/111827?text=App';
                      }}
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-white/90 border border-gray-200 text-gray-800">
                        {app.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="text-xl font-extrabold text-[#141414] group-hover:underline">{app.title}</div>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{app.description}</p>
                    <div className="mt-5 text-sm font-bold text-[#141414]">{t('apps.view_more')}</div>
                  </div>
                </>
              );

              if (app.external) {
                return (
                  <a
                    key={app.title}
                    href={app.href}
                    target="_blank"
                    rel="noreferrer"
                    className="group bg-white border border-gray-200 rounded-3xl overflow-hidden hover:shadow-lg transition-shadow duration-300"
                  >
                    {cardContent}
                  </a>
                );
              }

              return (
                <Link
                  key={app.title}
                  to={app.href}
                  className="group bg-white border border-gray-200 rounded-3xl overflow-hidden hover:shadow-lg transition-shadow duration-300"
                >
                  {cardContent}
                </Link>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AppsPage;
